import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import AddTransactionModal from '../components/AddTransactionModal';
import TransactionList from '../components/TransactionList';
import DateRangeDisplay from '../components/DateRangeDisplay';
import { transactionAPI, ledgerAPI, categoryAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiLock, FiFilter } from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, subMonths, isToday, isYesterday } from 'date-fns';
import CURRENCY, { formatSignedAmount, formatAmount, CurrencyIcon } from '../utils/currency';
import { loadPrefs, savePrefs, autoLayout } from '../utils/layoutPrefs';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

const LOCK_WINDOW_HOURS = 24;
const DETAILED_VIEW_KEY = 'hisab_detailed_txn_view';

const BALANCE_AFFECTING = ['income', 'expense', 'expense_paid', 'income_received', 'transfer', 'self_transfer', 'internal_transfer'];

/**
 * ENTERPRISE RULE: Transactions represent ONLY real money movement
 * Non-balance-affecting types (payable, receivable, scheduled) are handled in Vendor module
 */
const TRANSACTION_TYPE_KEYS = [
  { value: 'expense_paid', labelKey: 'transaction.expense_paid', affects_balance: true },
  { value: 'income_received', labelKey: 'transaction.income_received', affects_balance: true },
  { value: 'self_transfer', labelKey: 'transaction.self_transfer', affects_balance: true },
];

/**
 * ENTERPRISE RULE (Section 16): Internal transfers use neutral styling
 * - NOT red (expense)
 * - NOT green (income)
 * - USE neutral gray (#7F8C8D) or neutral purple (#6366F1)
 */
const isInternalTransfer = (type) => type === 'internal_transfer' || type === 'self_transfer';

const isTransactionLocked = (txn) => {
  const createdAt = new Date(txn.created_at);
  const lockExpiry = new Date(createdAt.getTime() + LOCK_WINDOW_HOURS * 60 * 60 * 1000);
  return new Date() > lockExpiry;
};

const isIncomeType = (type) => type === 'income' || type === 'income_received';

/**
 * ENTERPRISE RULE: Neutral types moved to Vendor module
 * Keep function for backward compatibility with existing data display
 */
const isNeutralType = (type) => ['payable', 'receivable', 'scheduled', 'internal_transfer'].includes(type);

/**
 * ENTERPRISE RULE (Section 15-17): Internal transfers display as bank-like entries
 * When txn is provided, shows direction-aware display for internal_transfer
 */
const getTypeInfo = (type, toneEnabled = false, t = (key) => key, txn = null) => {
  // Handle internal_transfer with direction awareness for bank-statement style display
  if (type === 'internal_transfer' && txn) {
    const desc = (txn.description || '').toLowerCase();
    // "Transfer to X" means money went OUT → expense-like
    if (desc.startsWith('transfer to')) {
      return {
        label: toneEnabled ? 'Transfer bheja' : 'Transfer Out',
        icon: '−',
        color: '#E74C3C',
        class: 'expense'
      };
    }
    // "Transfer from X" means money came IN → income-like
    if (desc.startsWith('transfer from')) {
      return {
        label: toneEnabled ? 'Transfer aaya' : 'Transfer In',
        icon: '+',
        color: '#27AE60',
        class: 'income'
      };
    }
  }

  const types = {
    income: { label: toneEnabled ? 'Payment aaya' : 'Income', icon: '+', color: '#27AE60', class: 'income' },
    income_received: { label: toneEnabled ? 'Payment aaya' : t('transaction.income_received'), icon: '+', color: '#27AE60', class: 'income' },
    expense: { label: toneEnabled ? 'Kharcha kiya' : 'Expense', icon: '−', color: '#E74C3C', class: 'expense' },
    expense_paid: { label: toneEnabled ? 'Kharcha kiya' : t('transaction.expense_paid'), icon: '−', color: '#E74C3C', class: 'expense' },
    self_transfer: { label: toneEnabled ? 'Apne mein' : 'Internal Transfer', icon: '↔', color: '#7F8C8D', class: 'transfer' },
    internal_transfer: { label: toneEnabled ? 'Transfer' : 'Internal Transfer', icon: '↔', color: '#7F8C8D', class: 'transfer' },
    payable: { label: toneEnabled ? 'Dena hai' : 'Payable', icon: '◯', color: '#F39C12', class: 'neutral' },
    receivable: { label: toneEnabled ? 'Lena hai' : 'Receivable', icon: '◎', color: '#3498DB', class: 'neutral' },
    scheduled: { label: toneEnabled ? 'Yaad' : 'Scheduled', icon: '⏰', color: '#9B59B6', class: 'neutral' },
  };
  return types[type] || { label: type, icon: '•', color: '#7F8C8D', class: 'neutral' };
};

const Transactions = () => {
  const { t, toneEnabled } = useTone();
  const [transactions, setTransactions] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false); // New unified modal for adding
  const [showEditModal, setShowEditModal] = useState(false); // Edit modal (inline form)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    ledger_id: '',
    category_id: '',
    transaction_type: 'expense_paid',
    amount: '',
    description: '',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [editingId, setEditingId] = useState(null);

  // View toggle
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(DETAILED_VIEW_KEY) === 'true' ? 'detailed' : 'modern');
  const [viewPrefs, setViewPrefs] = useState(() => {
    const prefs = loadPrefs();
    if (!prefs._manual) prefs.layout = autoLayout();
    return prefs;
  });
  const [expandedTxnId, setExpandedTxnId] = useState(null);

  const isGrid = viewPrefs.layout === 'grid';
  const isCompactDensity = viewPrefs.density === 'compact';

  // Date filter state
  const [dateFilter, setDateFilter] = useState('current_month');
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'current_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'last_3_months':
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'all':
      default:
        return { start: null, end: null };
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchData();
  }, [dateFilter]);

  const fetchData = async () => {
    try {
      const params = { limit: 50 };
      if (dateRange.start) params.start_date = format(dateRange.start, 'yyyy-MM-dd');
      if (dateRange.end) params.end_date = format(dateRange.end, 'yyyy-MM-dd');

      const [txnRes, ledgersRes, categoriesRes] = await Promise.allSettled([
        transactionAPI.getAll(params),
        ledgerAPI.getAll(),
        categoryAPI.getAll(),
      ]);
      if (txnRes.status === 'fulfilled') {
        setTransactions(txnRes.value.data.data.transactions || []);
      }
      if (ledgersRes.status === 'fulfilled') {
        const allLedgers = ledgersRes.value.data.data.ledgers || [];
        setLedgers(allLedgers);
        // Auto-select first active ledger
        const active = allLedgers.filter(l => !l.is_archived);
        if (active.length > 0 && !formData.ledger_id) {
          setFormData(prev => ({ ...prev, ledger_id: active[0].id }));
        }
      }
      if (categoriesRes.status === 'fulfilled') {
        setCategories(categoriesRes.value.data.data.categories || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const txnType = formData.transaction_type;
    const needsLedger = BALANCE_AFFECTING.includes(txnType);

    if (needsLedger && !formData.ledger_id) {
      toast.error('Ledger is required for this transaction type.');
      return;
    }

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        ledger_id: formData.ledger_id || undefined,
      };

      if (editingId) {
        await transactionAPI.update(editingId, submitData);
        toast.success('Transaction updated');
      } else {
        await transactionAPI.create(submitData);
        toast.success('Transaction created');
      }
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('This transaction is locked (older than 24 hours) and cannot be modified.');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  };

  const handleEdit = (txn) => {
    if (isTransactionLocked(txn)) {
      toast.error('This transaction is locked (older than 24 hours) and cannot be edited.');
      return;
    }
    setFormData({
      ledger_id: txn.ledger_id || '',
      category_id: txn.category_id || '',
      transaction_type: txn.transaction_type,
      amount: txn.amount,
      description: txn.description || '',
      transaction_date: txn.transaction_date.split('T')[0],
      notes: txn.notes || '',
    });
    setEditingId(txn.id);
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await transactionAPI.delete(deleteTarget);
      toast.success('Transaction deleted');
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('This transaction is locked (older than 24 hours) and cannot be deleted.');
      } else {
        toast.error('Failed to delete');
      }
    } finally {
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    const activeLedgers = ledgers.filter(l => !l.is_archived);
    setFormData({
      ledger_id: activeLedgers[0]?.id || '',
      category_id: '',
      transaction_type: 'expense_paid',
      amount: '',
      description: '',
      transaction_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const getTypeCategory = (type) => {
    if (type === 'income_received' || type === 'income') return 'expense';
    return 'expense';
  };

  // ENTERPRISE RULE: Filter out "Internal Transfer" system category - it's for system use only
  const filteredCategories = categories.filter(c => {
    // Exclude Internal Transfer system category from user selection
    if (c.is_system && c.name === 'Internal Transfer') return false;
    const txnType = formData.transaction_type;
    if (isIncomeType(txnType)) return c.category_type === 'income';
    return c.category_type === 'expense';
  });

  const activeLedgers = ledgers.filter(l => !l.is_archived);
  const needsLedger = BALANCE_AFFECTING.includes(formData.transaction_type);

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message="Loading transactions..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><CurrencyIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('transactions.title')}</h1>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {t('transactions.add')}
          </button>
        </div>

        {/* Date Filter, View Toggle & Range Display */}
        <div className="txn-filters-bar">
          <div className="txn-filters-left">
            <FiFilter size={16} style={{ color: 'var(--color-text-secondary)' }} />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="select-input"
            >
              <option value="current_month">{toneEnabled ? 'Is Mahine' : 'Current Month'}</option>
              <option value="last_month">{toneEnabled ? 'Pichle Mahine' : 'Last Month'}</option>
              <option value="last_3_months">{toneEnabled ? 'Pichle 3 Mahine' : 'Last 3 Months'}</option>
              <option value="all">{toneEnabled ? 'Sab Dikhaao' : 'All Time'}</option>
            </select>
            <div className="txn-view-toggle">
              <button
                className={`txn-view-btn${viewMode === 'modern' ? ' txn-view-btn--active' : ''}`}
                onClick={() => { setViewMode('modern'); localStorage.setItem(DETAILED_VIEW_KEY, 'false'); }}
              >
                Modern
              </button>
              <button
                className={`txn-view-btn${viewMode === 'detailed' ? ' txn-view-btn--active' : ''}`}
                onClick={() => { setViewMode('detailed'); localStorage.setItem(DETAILED_VIEW_KEY, 'true'); }}
              >
                Detailed
              </button>
              {viewMode === 'modern' && (
                <button
                  className={`txn-view-btn${isGrid ? ' txn-view-btn--active' : ''}`}
                  onClick={() => {
                    const nextLayout = isGrid ? 'list' : 'grid';
                    const nextDensity = nextLayout === 'grid' ? 'compact' : 'normal';
                    setViewPrefs(savePrefs({ layout: nextLayout, density: nextDensity }));
                    setExpandedTxnId(null);
                  }}
                  title={isGrid ? 'Switch to list layout' : 'Switch to grid layout'}
                >
                  {isGrid ? '☰' : '⊞'}
                </button>
              )}
            </div>
          </div>
          {dateRange.start && dateRange.end && (
            <DateRangeDisplay startDate={dateRange.start} endDate={dateRange.end} toneEnabled={toneEnabled} />
          )}
        </div>

        {transactions.length === 0 ? (
          <EmptyState
            icon={<CurrencyIcon />}
            title={t('transactions.no_transactions')}
            message={t('transactions.add_first_msg')}
            actionLabel={t('transactions.add')}
            onAction={() => setShowAddModal(true)}
          />
        ) : viewMode === 'detailed' ? (
          <div className="card">
            <TransactionList
              transactions={transactions}
              columns={['date', 'ledger', 'category', 'description', 'type', 'amount', 'actions']}
              onEdit={handleEdit}
              onDelete={(txn) => setDeleteTarget(txn.id)}
              isTransactionLocked={isTransactionLocked}
              getTypeInfo={(type, txn) => getTypeInfo(type, toneEnabled, t, txn)}
              formatDate={formatDate}
              t={t}
              toneEnabled={toneEnabled}
              showActions={true}
            />
          </div>
        ) : (
          <div className="card">
            <div className={`modern-txn-list${isGrid ? ' txn-grid' : ''}${isCompactDensity ? ' txn-density-compact' : ' txn-density-normal'}`}>
              {transactions.map((txn) => {
                const typeInfo = getTypeInfo(txn.transaction_type, toneEnabled, t, txn);
                const categoryName = txn.category_name || txn.description || typeInfo.label;
                const locked = isTransactionLocked(txn);
                const smartDate = (() => {
                  try {
                    const d = new Date(txn.transaction_date);
                    if (isToday(d)) return 'Today';
                    if (isYesterday(d)) return 'Yesterday';
                    return format(d, 'dd MMM');
                  } catch { return txn.transaction_date; }
                })();
                const isExpanded = expandedTxnId === txn.id;
                return (
                  <div
                    key={txn.id}
                    className={`modern-txn-row modern-txn-row--interactive${isGrid ? ' txn-card-compact' : ''}${isExpanded ? ' txn-card-expanded' : ''}`}
                    onClick={isGrid ? () => setExpandedTxnId(isExpanded ? null : txn.id) : undefined}
                  >
                    <div className="modern-txn-icon">{typeInfo.icon}</div>
                    <div className="modern-txn-info">
                      <span className="modern-txn-name">{categoryName}</span>
                      <span className="modern-txn-meta">
                        {smartDate}
                        {!isGrid && txn.ledger_name && ` · ${txn.ledger_name}`}
                        {!isGrid && txn.category_name && txn.description && ` · ${txn.description}`}
                      </span>
                    </div>
                    <div className="modern-txn-right">
                      <span className="modern-txn-amount" style={{ color: typeInfo.color }}>
                        {typeInfo.icon === '+' ? '+' : typeInfo.icon === '−' ? '−' : ''}{formatAmount(txn.amount)}
                      </span>
                      {!locked && !isGrid && (
                        <div className="modern-txn-actions">
                          <button className="btn-icon" onClick={() => handleEdit(txn)} title={t('action.edit')}><FiEdit2 size={14} /></button>
                          <button className="btn-icon danger" onClick={() => setDeleteTarget(txn.id)} title={t('action.delete')}><FiTrash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                    {isGrid && isExpanded && (
                      <div className="txn-expanded-details">
                        {txn.description && <span className="txn-expanded-desc">{txn.description}</span>}
                        {txn.ledger_name && <span className="txn-expanded-desc">{txn.ledger_name}</span>}
                        {!locked && (
                          <div className="txn-expanded-actions">
                            <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleEdit(txn); }} title={t('action.edit')}><FiEdit2 size={13} /></button>
                            <button className="btn-icon danger" onClick={(e) => { e.stopPropagation(); setDeleteTarget(txn.id); }} title={t('action.delete')}><FiTrash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Edit Transaction Modal (inline form for editing existing transactions) */}
        {showEditModal && (
          <div className="modal" onClick={() => { setShowEditModal(false); resetForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{t('transactions.edit')}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('transactions.type')} *</label>
                  <select name="transaction_type" value={formData.transaction_type} onChange={handleChange}>
                    {TRANSACTION_TYPE_KEYS.map(tt => (
                      <option key={tt.value} value={tt.value}>{t(tt.labelKey)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('transactions.ledger')} {needsLedger ? '*' : `(${t('common.optional')})`}</label>
                  <select name="ledger_id" value={formData.ledger_id} onChange={handleChange} required={needsLedger}>
                    {!needsLedger && <option value="">{t('transactions.no_ledger')}</option>}
                    {activeLedgers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('transactions.category')}</label>
                  <select name="category_id" value={formData.category_id} onChange={handleChange}>
                    <option value="">{t('transactions.select_category')}</option>
                    {filteredCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('transactions.amount')} ({CURRENCY.symbol}) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('transactions.date')}</label>
                  <input type="date" name="transaction_date" value={formData.transaction_date} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>{t('transactions.description')}</label>
                  <input type="text" name="description" value={formData.description} onChange={handleChange} placeholder="What was this for?" />
                </div>
                <div className="form-group">
                  <label>{t('transactions.notes')}</label>
                  <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" placeholder="Additional notes (optional)" />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {t('action.update')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={`${t('action.delete')} ${t('transactions.title')}`}
            message={t('transactions.delete_confirm')}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}

        {/* Add Transaction Modal (unified form, same as mobile) */}
        <AddTransactionModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => fetchData()}
        />
      </div>
    </Layout>
  );
};

export default Transactions;
